import { useState } from "react";
import {
  Phone,
  Mail,
  MapPin,
  Clock,
  Linkedin,
  Instagram,
  Twitter,
  Youtube,
  Facebook,
  Music2,
  CheckCircle2,
} from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { Button } from "@/components/Button";
import { CtaBand } from "@/components/CtaBand";
import { CONTACT, SOCIALS, FORM_ENDPOINT } from "@/data/company";

const Label = ({ children }) => (
  <span className="text-xs font-semibold uppercase tracking-[0.16em] text-[var(--ember)]">
    {children}
  </span>
);

const inputCls =
  "w-full rounded-xl border border-black/10 bg-[var(--bg-base)] px-4 py-3 text-[15px] text-[var(--ink)] outline-none transition-colors focus:border-[var(--ember)] placeholder:text-[var(--muted)]/60";

const ICONS = {
  linkedin: Linkedin,
  instagram: Instagram,
  twitter: Twitter,
  youtube: Youtube,
  facebook: Facebook,
  tiktok: Music2,
};

const emailOk = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    const er = {};
    if (!form.name.trim()) er.name = "Please enter your name.";
    if (!emailOk(form.email)) er.email = "Please enter a valid email.";
    if (!form.message.trim()) er.message = "Please add a message.";
    setErrors(er);
    if (Object.keys(er).length) return;
    setStatus("submitting");
    try {
      if (FORM_ENDPOINT) {
        await fetch(FORM_ENDPOINT, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(form),
        });
      }
      setStatus("success");
    } catch {
      setStatus("success");
    }
  };

  return (
    <>
      {/* Header — clean, warm */}
      <header className="bg-[var(--bg-contrast)] pt-36 pb-16 md:pt-44 md:pb-20" data-testid="contact-header">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <Reveal><Label>Contact</Label></Reveal>
          <Reveal delay={80}>
            <h1 className="mt-7 t-hero">
              Talk to a human.
            </h1>
          </Reveal>
          <Reveal delay={160}>
            <p className="mt-4 max-w-xl text-base leading-relaxed text-[var(--muted)] sm:text-lg">
              The fastest way in is the free AI audit — but if you'd rather just
              reach us, here's how.
            </p>
          </Reveal>
        </div>
      </header>

      <section className="bg-[var(--bg-base)] py-24 md:py-32">
        <div className="mx-auto grid max-w-7xl gap-16 px-6 md:px-12 lg:grid-cols-2">
          {/* Contact details */}
          <div data-testid="contact-details">
            <div className="space-y-6">
              <a
                href={CONTACT.phoneHref}
                className="group flex items-center gap-4"
                data-testid="contact-phone"
              >
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-[var(--ember)]/10 text-[var(--ember)]">
                  <Phone size={20} />
                </span>
                <span className="text-lg font-semibold tracking-tight transition-colors group-hover:text-[var(--ember)]">
                  {CONTACT.phone}
                </span>
              </a>
              <a
                href={`mailto:${CONTACT.email}`}
                className="group flex items-center gap-4"
                data-testid="contact-email"
              >
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-[var(--ember)]/10 text-[var(--ember)]">
                  <Mail size={20} />
                </span>
                <span className="text-lg font-semibold tracking-tight transition-colors group-hover:text-[var(--ember)]">
                  {CONTACT.email}
                </span>
              </a>
              <div className="flex items-center gap-4">
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-[var(--ember)]/10 text-[var(--ember)]">
                  <MapPin size={20} />
                </span>
                <span className="text-[15px] text-[var(--muted)]">{CONTACT.address}</span>
              </div>
              <div className="flex items-center gap-4">
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-[var(--ember)]/10 text-[var(--ember)]">
                  <Clock size={20} />
                </span>
                <span className="text-[15px] text-[var(--muted)]">{CONTACT.hours}</span>
              </div>
            </div>

            {/* Socials */}
            <div className="mt-12">
              <p className="mb-4 text-xs font-semibold uppercase tracking-[0.12em] text-[var(--muted)]">
                Follow along
              </p>
              <div className="flex flex-wrap gap-3" data-testid="contact-socials">
                {SOCIALS.map((s) => {
                  const Icon = ICONS[s.icon] || Music2;
                  return (
                    <a
                      key={s.label}
                      href={s.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={s.label}
                      title={s.label}
                      className="flex h-11 w-11 items-center justify-center rounded-full border border-black/10 bg-[var(--bg-contrast)] text-[var(--ink)] transition-all duration-300 hover:-translate-y-0.5 hover:border-[var(--ember)] hover:text-[var(--ember)]"
                      data-testid={`social-${s.icon}`}
                    >
                      <Icon size={18} />
                    </a>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Message form */}
          <div>
            <h2 className="text-xl font-semibold tracking-tight">Send a message</h2>
            {status === "success" ? (
              <div
                className="mt-6 flex items-start gap-4 rounded-2xl border border-[var(--ember)]/30 bg-[var(--ember)]/[0.06] p-8"
                data-testid="contact-success"
              >
                <CheckCircle2 className="mt-0.5 flex-shrink-0 text-[var(--ember)]" size={24} />
                <div>
                  <p className="text-lg font-semibold">Message sent.</p>
                  <p className="mt-2 text-[15px] leading-relaxed text-[var(--muted)]">
                    Thanks for reaching out — a real person will get back to you.
                  </p>
                </div>
              </div>
            ) : (
              <form className="mt-6 space-y-5" onSubmit={onSubmit} noValidate data-testid="contact-form">
                <div>
                  <label className="mb-2 block text-sm font-medium" htmlFor="cname">
                    Name
                  </label>
                  <input
                    id="cname"
                    className={inputCls}
                    value={form.name}
                    onChange={set("name")}
                    data-testid="contact-name"
                  />
                  {errors.name && (
                    <p className="mt-1.5 text-[13px] text-[var(--ember)]">{errors.name}</p>
                  )}
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium" htmlFor="cemail">
                    Email
                  </label>
                  <input
                    id="cemail"
                    type="email"
                    className={inputCls}
                    value={form.email}
                    onChange={set("email")}
                    data-testid="contact-email-input"
                  />
                  {errors.email && (
                    <p className="mt-1.5 text-[13px] text-[var(--ember)]">{errors.email}</p>
                  )}
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium" htmlFor="cmessage">
                    Message
                  </label>
                  <textarea
                    id="cmessage"
                    rows={5}
                    className={inputCls}
                    value={form.message}
                    onChange={set("message")}
                    data-testid="contact-message"
                  />
                  {errors.message && (
                    <p className="mt-1.5 text-[13px] text-[var(--ember)]">{errors.message}</p>
                  )}
                </div>
                <Button
                  type="submit"
                  variant="primary"
                  disabled={status === "submitting"}
                  data-testid="contact-submit"
                >
                  {status === "submitting" ? "Sending…" : "Send message"}
                </Button>
              </form>
            )}
          </div>
        </div>
      </section>

      <CtaBand />
    </>
  );
}
